import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import './WeeklyAdPage.css';

interface WeeklyAdItem {
  id: string;
  title: string;
  description: string;
  price: string;
  originalPrice?: string;
  discount?: string;
  image: string;
  category: string;
  validUntil: string;
}

const WeeklyAdPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Sample weekly ad items with placeholder images
  const weeklyAdItems: WeeklyAdItem[] = [
    {
      id: '1',
      title: 'Fresh Organic Strawberries',
      description: 'Sweet and juicy, perfect for breakfast',
      price: '₹331',
      originalPrice: '₹497',
      discount: '33% OFF',
      image: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=400&h=400&fit=crop',
      category: 'Produce',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '2',
      title: 'Premium Ground Beef',
      description: '100% grass-fed, 1 lb package',
      price: '₹580',
      originalPrice: '₹829',
      discount: '30% OFF',
      image: 'https://images.unsplash.com/photo-1603048297172-c92544798d5a?w=400&h=400&fit=crop',
      category: 'Meat',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '3',
      title: 'Fresh Atlantic Salmon',
      description: 'Wild-caught, 1 lb fillet',
      price: '₹1,078',
      originalPrice: '₹1,410',
      discount: '24% OFF',
      image: 'https://images.unsplash.com/photo-1544943910-4c1dc44aab44?w=400&h=400&fit=crop',
      category: 'Seafood',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '4',
      title: 'Organic Whole Milk',
      description: '1 gallon, hormone-free',
      price: '₹414',
      originalPrice: '₹539',
      discount: '23% OFF',
      image: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=400&h=400&fit=crop',
      category: 'Dairy',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '5',
      title: 'Artisan Sourdough Bread',
      description: 'Fresh baked daily, 1 lb loaf',
      price: '₹290',
      originalPrice: '₹414',
      discount: '30% OFF',
      image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&h=400&fit=crop',
      category: 'Bakery',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '6',
      title: 'Fresh Baby Spinach',
      description: 'Organic, 10 oz bag',
      price: '₹248',
      originalPrice: '₹373',
      discount: '33% OFF',
      image: 'https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=400&h=400&fit=crop',
      category: 'Produce',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '7',
      title: 'Free-Range Chicken Breast',
      description: 'Boneless, skinless, 1.5 lbs',
      price: '₹746',
      originalPrice: '₹1,078',
      discount: '31% OFF',
      image: 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=400&h=400&fit=crop',
      category: 'Meat',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '8',
      title: 'Fresh Shrimp',
      description: 'Large, 1 lb frozen',
      price: '₹829',
      originalPrice: '₹1,161',
      discount: '29% OFF',
      image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400&h=400&fit=crop',
      category: 'Seafood',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '9',
      title: 'Greek Yogurt',
      description: 'Plain, 32 oz container',
      price: '₹373',
      originalPrice: '₹497',
      discount: '25% OFF',
      image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=400&h=400&fit=crop',
      category: 'Dairy',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '10',
      title: 'Fresh Croissants',
      description: 'Butter, 4-pack',
      price: '₹497',
      originalPrice: '₹663',
      discount: '25% OFF',
      image: 'https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=400&h=400&fit=crop',
      category: 'Bakery',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '11',
      title: 'Organic Tomatoes',
      description: 'Vine-ripened, 1 lb',
      price: '₹207',
      originalPrice: '₹331',
      discount: '38% OFF',
      image: 'https://images.unsplash.com/photo-1546470427-e26264be0f42?w=400&h=400&fit=crop',
      category: 'Produce',
      validUntil: 'Valid until Jan 15, 2024'
    },
    {
      id: '12',
      title: 'Premium Ribeye Steak',
      description: 'USDA Choice, 1 lb',
      price: '₹1,245',
      originalPrice: '₹1,660',
      discount: '25% OFF',
      image: 'https://images.unsplash.com/photo-1603048297172-c92544798d5a?w=400&h=400&fit=crop',
      category: 'Meat',
      validUntil: 'Valid until Jan 15, 2024'
    }
  ];

  const categories = ['all', 'Produce', 'Meat', 'Seafood', 'Dairy', 'Bakery'];

  const filteredItems = selectedCategory === 'all' 
    ? weeklyAdItems 
    : weeklyAdItems.filter(item => item.category === selectedCategory);

  const handleItemClick = (item: WeeklyAdItem) => {
    // Navigate to product page or add to cart
    navigate('/browse-all', { 
      state: { 
        searchQuery: item.title,
        filterType: 'search' 
      } 
    });
  };

  return (
    <div className="weekly-ad-page">
      {/* Header */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate(-1)}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />

      {/* Hero Section */}
      <div className="weekly-ad-hero">
        <div className="hero-content">
          <h1 className="page-title">Weekly Ad</h1>
          <p className="page-subtitle">Valid January 8 - January 14, 2024</p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="category-filter">
        <div className="filter-container">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category === 'all' ? 'All Items' : category}
            </button>
          ))}
        </div>
      </div>

      {/* Ad Items Grid */}
      <div className="weekly-ad-container">
        <div className="ad-items-grid">
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className="ad-item-card"
              onClick={() => handleItemClick(item)}
            >
              {item.discount && (
                <div className="discount-badge">{item.discount}</div>
              )}
              <div className="item-image-container">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="item-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://via.placeholder.com/300x300?text=${encodeURIComponent(item.title)}`;
                  }}
                />
              </div>
              <div className="item-content">
                <div className="item-category">{item.category}</div>
                <h3 className="item-title">{item.title}</h3>
                <p className="item-description">{item.description}</p>
                <div className="item-pricing">
                  {item.originalPrice && (
                    <span className="original-price">{item.originalPrice}</span>
                  )}
                  <span className="current-price">{item.price}</span>
                </div>
                <div className="item-validity">{item.validUntil}</div>
                <button className="add-to-cart-btn">Add to Cart</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Info */}
      <div className="weekly-ad-footer">
        <div className="footer-content">
          <p className="footer-text">
            Prices valid at participating locations. Some items may not be available in all stores. 
            Quantities limited. No rain checks. See store for details.
          </p>
          <p className="footer-text">
            © 2024 Quickko. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WeeklyAdPage;

